/**
 * نمای نتیجه‌ی قضاوت مقایسه‌ای.
 *
 * ترتیب بخش‌ها از قاطع‌ترین به تفصیلی‌ترین است. سرتیتر دو چیز را با هم می‌گوید:
 * جمع‌بندی و **گزینه‌ی نزدیک‌تر**. این دو یکی نیستند و جدا نوشتن‌شان لازم است:
 * پرسش کاربر «به کدام نزدیک‌تر است» بود، اما پاسخ می‌تواند «هیچ‌کدام» یا «ترکیب
 * بهتر» باشد و در آن حالت هم نزدیک‌ترین گزینه باید معلوم بماند.
 */

import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Brain,
  Gauge,
  GitMerge,
  ListChecks,
  Quote,
  RefreshCw,
  Table,
} from 'lucide-react'

import { Badge, Bullets, Card, Ring, SectionHeader } from '@/components/ui/primitives'
import { labelOf } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import { DimensionalScorecard } from './DimensionalScorecard'

import type { DecisionJudgment, JudgmentInput } from '@/lib/types'

interface JudgmentResultProps {
  judgment: DecisionJudgment
  request: JudgmentInput
}

export const JudgmentResult = ({ judgment, request }: JudgmentResultProps) => {
  const { meta } = useMeta()
  const selection = labelOf(meta.labels, 'judgment_selection', judgment.selection)
  const closest = labelOf(meta.labels, 'closest_option', judgment.closest_option)
  const titleA = request.option_a.title.trim()
  const titleB = request.option_b.title.trim()

  return (
    <div className="space-y-4">
      <Card tone="feature">
        <div className="flex flex-wrap items-center justify-between gap-5 p-5">
          <div className="min-w-0 space-y-1.5">
            <p className="text-xs font-medium text-ink-muted">جمع‌بندی</p>
            <p className="text-2xl leading-tight font-bold tracking-tight text-ink">
              {selection}
            </p>
            {/*
              گزینه‌ی نزدیک‌تر جدا از جمع‌بندی نوشته می‌شود، چون حتی وقتی
              جمع‌بندی «هیچ‌کدام» است، پرسش اصلی کاربر همین است.
            */}
            <p className="text-sm text-ink-muted">
              نزدیک‌ترین گزینه به منطق تصمیم‌گیر: <Badge tone="neutral">{closest}</Badge>
            </p>
          </div>
          <Ring value={judgment.confidence} label="اطمینان" size={80} />
        </div>
        <div className="border-t border-line p-5">
          <p className="text-lg leading-relaxed font-medium text-ink">
            {judgment.direct_judgment}
          </p>
          {judgment.recommended_next_action !== '' && (
            <p className="mt-4 flex gap-2.5 rounded-md border border-accent-line bg-accent-soft/60 p-3.5 text-sm leading-relaxed text-ink">
              <ArrowRight className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
              <span>
                <span className="font-semibold">اقدام بعدی: </span>
                {judgment.recommended_next_action}
              </span>
            </p>
          )}
        </div>
      </Card>

      {judgment.why_closer !== '' && (
        <Card>
          <SectionHeader
            title="چرا نزدیک‌تر است"
            caption="تفاوت مشخصی که انتخاب را می‌سازد، نه فهرست مزیت‌های عمومی."
            icon={<BadgeCheck className="size-4" />}
          />
          <p className="p-5 leading-relaxed text-ink">{judgment.why_closer}</p>
        </Card>
      )}

      {/* پیشنهاد ترکیب فقط وقتی وجود دارد که جمع‌بندی ترکیب باشد؛ سرور آن را
          در حالت‌های دیگر پاک می‌کند. */}
      {judgment.hybrid_proposal !== '' && (
        <Card>
          <SectionHeader
            title="پیشنهاد ترکیب بهتر"
            caption="چه چیزی از الف، چه چیزی از ب، و چه چیزی حذف می‌شود."
            icon={<GitMerge className="size-4" />}
          />
          <p className="p-5 leading-relaxed text-ink">{judgment.hybrid_proposal}</p>
        </Card>
      )}

      <Card>
        <SectionHeader
          title="کارت امتیاز ابعادی"
          caption="هر بُعد برای هر دو گزینه امتیاز و دلیل دارد. جمع وزنی نمایش داده نمی‌شود؛ تصمیم روی بُعدهای وزن‌دار گرفته می‌شود، نه روی میانگین."
          icon={<Table className="size-4" />}
        />
        <DimensionalScorecard rows={judgment.scorecard} />
      </Card>

      <Card>
        <SectionHeader
          title={`برآورد منطق تصمیم ${meta.profile.principal_name}`}
          icon={<Brain className="size-4" />}
        />
        <p className="p-5 leading-relaxed text-ink">{judgment.likely_principal_view}</p>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <Bullets
            title={titleA === '' ? 'ریسک‌های تصمیم الف' : `ریسک‌های الف: ${titleA}`}
            items={judgment.option_a_risks}
            icon={<AlertTriangle className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title={titleB === '' ? 'ریسک‌های تصمیم ب' : `ریسک‌های ب: ${titleB}`}
            items={judgment.option_b_risks}
            icon={<AlertTriangle className="size-3.5" aria-hidden />}
          />
        </Card>
        {/* ریسک مشترک جدا می‌ماند: چیزی که با هر دو انتخاب باقی می‌ماند، مبنای
            انتخاب نیست و نباید در ستون یک گزینه دیده شود. */}
        {judgment.shared_risks.length > 0 && (
          <Card className="p-5">
            <Bullets
              title="ریسک مشترک هر دو گزینه"
              items={judgment.shared_risks}
              icon={<AlertTriangle className="size-3.5" aria-hidden />}
            />
          </Card>
        )}
        <Card className="p-5">
          <Bullets
            title="شرط‌های لازم برای اجرا"
            items={judgment.required_conditions}
            icon={<ListChecks className="size-3.5" aria-hidden />}
          />
        </Card>
        <Card className="p-5">
          <Bullets
            title="چه چیزی انتخاب را برمی‌گرداند"
            items={judgment.what_would_flip_the_choice}
            icon={<RefreshCw className="size-3.5" aria-hidden />}
          />
        </Card>
      </div>

      <Card>
        <SectionHeader title="توضیح اطمینان" icon={<Gauge className="size-4" />} />
        <p className="p-5 text-sm leading-relaxed text-ink-muted">
          {judgment.confidence_explanation}
        </p>
      </Card>

      {judgment.historical_examples.length > 0 && (
        <Card>
          <SectionHeader title="نمونه‌های تاریخی" icon={<Quote className="size-4" />} />
          <ul className="space-y-3 p-5">
            {judgment.historical_examples.map((example) => (
              <li
                key={example.decision_id}
                className="rounded-md bg-surface-sunken p-3.5 text-sm leading-relaxed"
              >
                <Badge tone="neutral" className="tabular">
                  {example.decision_id}
                </Badge>
                <p className="mt-2 text-ink">{example.summary}</p>
                <p className="mt-1 text-ink-muted">{example.relevance}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
